import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { FingerprintIcon } from "./FingerprintIcon.js";
import { isPasskeySupported } from "../webauthn/index.js";
/**
 * The "— or — Continue with a passkey" affordance every login form in this monorepo
 * shows below its email/password fields — one shared component so the divider markup,
 * fingerprint badge, and "hide entirely on unsupported browsers" rule live in one place
 * rather than three-plus near-identical copies (dashboard-admin, platform-admin,
 * ecommerce-store's tenant-storefront AND marketplace login forms).
 *
 * Deliberately renders nothing at all — not a disabled button — when `isPasskeySupported()`
 * is false: a button that can only ever fail is worse than no button.
 *
 * `isPasskeySupported()` reads `window`/`PublicKeyCredential`, which don't exist during
 * SSR — checking it during render (rather than in an effect) made SSR always render `null`
 * while the client immediately re-rendered the real button, a server/client markup mismatch
 * that crashed hydration (React error #418) on every app's login page. Starting `false` and
 * flipping to the real value post-mount matches the pattern this codebase already uses for
 * `AuthContext`/`CartContext`.
 */
export function PasskeyLoginButton({ onLoginWithPasskey, label, disabled }) {
    const [pending, setPending] = useState(false);
    const [supported, setSupported] = useState(false);
    useEffect(() => {
        setSupported(isPasskeySupported());
    }, []);
    if (!supported)
        return null;
    async function handleClick() {
        setPending(true);
        try {
            await onLoginWithPasskey();
        }
        catch {
            // The caller's own error state (e.g. AuthContext's loginError) already reflects this.
        }
        finally {
            setPending(false);
        }
    }
    return (_jsxs("div", { className: "sl-passkey-login", children: [_jsx("div", { className: "sl-passkey-login__divider", role: "separator", children: _jsx("span", { children: "or" }) }), _jsxs("button", { type: "button", className: "sl-passkey-login__button", disabled: disabled || pending, onClick: () => void handleClick(), children: [_jsx("span", { className: ["sl-passkey-login__badge", pending ? "sl-passkey-login__badge--pending" : null]
                            .filter(Boolean)
                            .join(" "), children: _jsx(FingerprintIcon, { className: "sl-passkey-login__glyph" }) }), _jsx("span", { className: "sl-passkey-login__label", children: pending ? "Waiting for your passkey…" : (label ?? "Continue with a passkey") })] })] }));
}
//# sourceMappingURL=PasskeyLoginButton.js.map